/**
 * Painel direito: o contexto comercial (H04, §11.2).
 *
 * ── A Técnica da task começa assim: "o painel direito é o produto" ────────
 *
 * *"É ele que o Chatwoot puro não tem, e é ele que exige a escada de identidade
 * funcionando. Painel vazio por falta de vínculo precisa dizer isso e oferecer
 * vincular manualmente — não ficar em branco."*
 *
 * E a Armadilha é o oposto exato: *"painel direito vazio e silencioso quando
 * não há vínculo. O vendedor conclui que o produto está quebrado — quando na
 * verdade a identidade ficou em revisão (D09)."*
 *
 * Por isso o bloco de vínculo vem ANTES de tudo quando há algo a dizer: "em
 * revisão" é temporário e não pede nada de ninguém; "sem vínculo" é ação
 * pendente. O "cliente identificado" saiu (v3): identificado é o caso normal,
 * e o painel inteiro já diz isso.
 *
 * ── O desenho (v3, #677) ──────────────────────────────────────────────────
 *
 * Topo com o avatar, o nome, o telefone com copiar e a situação. Abas
 * sublinhadas: Cliente, Atividades e Notas. Em Cliente, as seções de linhas
 * com ícone: Informações (nome, telefone, e-mail, origem, última interação,
 * tags) e Detalhes do atendimento (responsável, prioridade, fila, canal), e
 * as Ações rápidas embaixo. Em Atividades, o que a #641 trouxe: as
 * oportunidades do cliente, as tarefas futuras e quem da equipe já falou com
 * ele.
 *
 * ── A aba Agendamento (#1085) ─────────────────────────────────────────────
 *
 * Na clínica, o painel ganha a aba Agendamento, entre Cliente e Atividades:
 * o bloco "Consulta" (o botão de agendar) sai da aba Cliente e vai para lá,
 * junto com os "Preços" do sistema da clínica. É o que a recepção faz lendo a
 * conversa — "quanto custa?" e "tem horário?" —, e não dado de cadastro. O
 * "Paciente no SISAC" fica em Cliente: é o vínculo do cadastro. Sem agenda e
 * sem preços ligados, a aba não aparece.
 *
 * ── Por que a aba "Mais" saiu ────────────────────────────────────────────
 *
 * Ela guardava a prioridade e as ações rápidas, e a aba Cliente tinha espaço
 * sobrando embaixo. Prioridade escondida atrás de uma aba é prioridade que
 * ninguém muda; e "Criar tarefa" é o que se faz lendo a conversa, com a ficha
 * à vista. A prioridade virou linha ao lado do responsável, que é o mesmo
 * tipo de coisa: um dado do atendimento que se troca ali mesmo.
 */

import { useEffect, useState, type ReactNode } from 'react';
import type { ConversaComposta } from '@cliqcrm/contracts';
import { idadeEmPalavras } from '@/shared/ui/estados/idade';
import { prazoEmPalavras } from '@/entities/comercial/tarefa';
import { abertaPorExtenso, dinheiro } from '@/shared/lib/formato';
import { iconeDoCanal, nomeDoCanal } from '@/entities/atendimento/canal';
import { NOMES_DE_SITUACAO } from '@/entities/atendimento/conversa';
import {
  Avatar,
  Botao,
  Cartao,
  Chip,
  Icone,
  Segmentado,
  Situacao,
  idDoPainel,
  type NomeDoIcone,
} from '@/shared/ui';
import { nomeDaEtapa } from '@/entities/comercial/etapa';
import { Etiquetas } from '@/entities/atendimento/etiqueta';
import { EditorDeEtiquetasDaConversa } from '@/features/atendimento/editar-etiquetas';
import { NotasDoCliente } from './notas-do-cliente';
import { CamposDoCliente } from './campos-do-cliente';
import { SeletorDePrioridade } from '@/features/atendimento/mudar-prioridade';
import { SeletorDeResponsavel } from '@/features/atendimento/passar-para-outra-pessoa';
import { ContatoEditavel } from '@/features/clientes/editar-contato';
import {
  ATUALIZA_PRECOS_DA_CLINICA,
  EDITA_CLIENTE,
  EDITA_OPORTUNIDADE,
} from '@/entities/acesso/papel';
import { usePapel, usePreferencias, useSessao } from '@/shared/sessao/contexto';
import { PacienteNoSisac } from '@/features/saude/vincular-paciente';
import { pacienteNoSisacLigado } from '@/entities/saude/paciente';
import { AgendamentoNoPainel, AgendarConsulta } from './agendar-consulta';
import { PrecosDaClinica } from './precos-da-clinica';

/** O mesmo `id` no botão (`aria-controls`) e no corpo que ele abre e fecha. */
const CORPO_DO_PAINEL = 'painel-do-cliente-corpo';

type AbaDoPainel = 'cliente' | 'agendamento' | 'atividades' | 'notas';

/** O prefixo dos ids das abas e dos painéis — o `Segmentado` deriva os dois. */
const ABAS_DO_PAINEL = 'painel-do-cliente';

export function Painel({
  conversa,
  aoCriarOportunidade,
  aoCriarTarefa,
  aoAbrirCliente,
  ocupado,
  fechado = false,
  aoAlternar,
  agendando = false,
  aoAgendando,
  aoCopiarParaMensagem,
}: {
  conversa: ConversaComposta | undefined;
  aoCriarOportunidade: () => void;
  aoCriarTarefa: () => void;
  aoAbrirCliente: (clienteId: string) => void;
  ocupado: boolean;
  /**
   * Recolhido numa faixa estreita, para a conversa ganhar a largura (25/09/2026).
   *
   * Ausente `aoAlternar`, o painel não se recolhe: é o celular, em que o
   * cliente já é uma etapa inteira e recolher não teria para onde ir.
   */
  fechado?: boolean;
  aoAlternar?: () => void;
  /** O painel inteiro virou o agendamento da consulta (25/10); a tela alarga a coluna. */
  agendando?: boolean;
  aoAgendando?: (sim: boolean) => void;
  /** O "Copiar" dos preços: põe o texto na caixa de mensagem da conversa, sem enviar (#1085). */
  aoCopiarParaMensagem?: (texto: string) => void;
}): ReactNode {
  const recolhido = fechado && aoAlternar !== undefined;
  const papelDaSessao = usePapel();
  const podeEditarCliente = papelDaSessao !== null && EDITA_CLIENTE.has(papelDaSessao);
  const { estado } = useSessao();
  const { clinica: agendaDaClinica } = usePreferencias();
  // O paciente no SISAC (#1024): só com a clínica e a busca do paciente ligadas.
  const comClinica = estado.nome === 'autenticado' && (estado.me.flags['clinica'] ?? false);
  const comPacienteNoSisac = comClinica && pacienteNoSisacLigado(agendaDaClinica);
  // Agendar daqui: a clínica com as vagas e o agendar ligados, e quem edita a oportunidade.
  const podeAgendar =
    comClinica &&
    agendaDaClinica['vagas'] !== undefined &&
    agendaDaClinica['agendar'] !== undefined &&
    papelDaSessao !== null &&
    EDITA_OPORTUNIDADE.has(papelDaSessao);
  // Os preços (#1085): a clínica com ao menos uma das operações de preço ligada.
  const comPrecos =
    comClinica &&
    aoCopiarParaMensagem !== undefined &&
    (agendaDaClinica['precos_de_consulta'] !== undefined ||
      agendaDaClinica['precos_de_procedimentos'] !== undefined);
  const podeAtualizarPrecos =
    papelDaSessao !== null && ATUALIZA_PRECOS_DA_CLINICA.has(papelDaSessao);

  /*
   * O conteúdo das abas inativas fica no DOM com `hidden`, e não desmontado:
   * desmontado, o foco do teclado cairia no nada e o estado de quem estava
   * no meio de um editor de etiquetas se perderia junto. É a mesma regra do
   * bloco recolhível, pelo mesmo motivo.
   */
  const [aba, definirAba] = useState<AbaDoPainel>('cliente');
  const [avisoDaConsulta, definirAvisoDaConsulta] = useState<string | null>(null);

  /*
   * Recolhido, sobra só o botão que abre. O corpo SAI do DOM, e não só da
   * vista: escondido por CSS, ele continuaria na leitura de tela e no Tab —
   * dez controles invisíveis entre a conversa e o resto da página.
   */
  if (recolhido) {
    return (
      <aside className="col col--painel col--painel-fechado" aria-label="Contexto do cliente">
        <button
          type="button"
          className="painel-alternar painel-alternar--abrir"
          aria-expanded={false}
          aria-controls={CORPO_DO_PAINEL}
          aria-label="Mostrar o cliente"
          title="Mostrar o cliente"
          onClick={aoAlternar}
        >
          {/* O MESMO glifo do botão de recolher, girado pelo CSS: assim a seta
              anima de um estado para o outro em vez de trocar de desenho. */}
          <Icone nome="direita" />
          <span className="painel-alternar__rotulo">Cliente</span>
        </button>
      </aside>
    );
  }

  const recolher = aoAlternar && (
    <button
      type="button"
      className="painel-alternar"
      aria-expanded
      aria-controls={CORPO_DO_PAINEL}
      aria-label="Recolher o cliente"
      title="Recolher o cliente"
      onClick={aoAlternar}
    >
      <Icone nome="direita" />
    </button>
  );

  if (!conversa) {
    return (
      <aside className="col col--painel" aria-label="Contexto do cliente">
        <header className="col__cabecalho">
          <h2>Cliente</h2>
          {recolher}
        </header>
      </aside>
    );
  }

  const {
    vinculo,
    cliente,
    oportunidades,
    tarefas_futuras: tarefasFuturas,
    participantes,
  } = conversa;
  const { situacao } = conversa.conversa;
  const nome = cliente?.nome_exibicao ?? 'Contato sem cadastro';
  // Só o negócio em aberto recebe consulta, e cada um diz o funil e a etapa: o
  // paciente pode ter um card no agendamento e outro em outro funil (o dos exames).
  const paraAgendar = oportunidades
    .filter((o) => !o.encerrada)
    .map((o) => ({ id: o.id, titulo: o.titulo, etapa: o.etapa_nome ?? o.etapa }));
  const comConsulta = podeAgendar && cliente !== null && aoAgendando !== undefined;
  const comAgendamento = comConsulta || comPrecos;
  // A conversa nova pode não ter a aba que estava aberta: volta para Cliente.
  const abaAberta: AbaDoPainel = aba === 'agendamento' && !comAgendamento ? 'cliente' : aba;
  const contatoDoPaciente = cliente && {
    nome: cliente.nome_exibicao,
    telefone: cliente.telefone_exibicao,
    email: cliente.email,
  };

  if (agendando && podeAgendar && contatoDoPaciente) {
    return (
      <AgendamentoNoPainel
        oportunidades={paraAgendar}
        contato={contatoDoPaciente}
        canal={conversa.conversa.canal}
        aoAgendar={() => {
          definirAvisoDaConsulta('Consulta agendada no SISAC.');
          aoAgendando?.(false);
        }}
        aoFechar={() => aoAgendando?.(false)}
      />
    );
  }

  return (
    <aside className="col col--painel" aria-label="Contexto do cliente">
      <header className="painel-cliente__topo">
        <Avatar nome={cliente?.nome_exibicao} tamanho={56} cheio />
        <div className="painel-cliente__quem">
          <h2 className="painel-cliente__nome" title={nome}>
            {nome}
          </h2>
          {cliente?.telefone_exibicao && (
            <span className="painel-cliente__fone">
              {cliente.telefone_exibicao}
              <BotaoDeCopiar rotulo="telefone" valor={cliente.telefone_exibicao} />
            </span>
          )}
          <Situacao
            texto={NOMES_DE_SITUACAO[situacao] ?? situacao}
            tom={situacao === 'aberta' ? 'ok' : situacao === 'pendente' ? 'atencao' : 'neutro'}
          />
        </div>
        {recolher}
      </header>

      <div className="painel-cliente__abas">
        <Segmentado
          como="abas"
          aparencia="sublinhado"
          idDasAbas={ABAS_DO_PAINEL}
          rotulo="Seções do cliente"
          escolhida={abaAberta}
          aoEscolher={definirAba}
          opcoes={[
            { valor: 'cliente', rotulo: 'Cliente' },
            ...(comAgendamento ? [{ valor: 'agendamento' as const, rotulo: 'Agendamento' }] : []),
            { valor: 'atividades', rotulo: 'Atividades' },
            { valor: 'notas', rotulo: 'Notas' },
          ]}
        />
      </div>

      <div className="col__rolagem contexto" id={CORPO_DO_PAINEL}>
        <PainelDaAba valor="cliente" ativa={abaAberta}>
          <BlocoDeVinculo vinculo={vinculo} />
          <Informacoes conversa={conversa} />
          <CamposDoCliente
            conversa={conversa}
            podeEditar={podeEditarCliente}
            moldura={(conteudo) => <Bloco titulo="Campos do cliente">{conteudo}</Bloco>}
          />
          {comPacienteNoSisac && cliente && (
            <Bloco titulo="Paciente no SISAC">
              <PacienteNoSisac
                key={cliente.id}
                clienteId={cliente.id}
                contato={{
                  nome: cliente.nome_exibicao,
                  telefone: cliente.telefone_exibicao,
                  email: cliente.email,
                }}
                podeEditar={podeEditarCliente}
              />
            </Bloco>
          )}
          <Bloco titulo="Detalhes do atendimento">
            <DetalhesDoAtendimento conversa={conversa} />
          </Bloco>

          <Bloco titulo="Ações rápidas">
            <div className="acoes-rapidas">
              <Botao
                onClick={aoCriarOportunidade}
                desabilitado={ocupado || !cliente}
                motivo={
                  cliente
                    ? 'Aguarde: a ação anterior ainda está em curso.'
                    : 'Precisa de um contato cadastrado'
                }
              >
                Criar oportunidade
              </Botao>
              <Botao
                onClick={aoCriarTarefa}
                desabilitado={ocupado || !cliente}
                motivo={
                  cliente
                    ? 'Aguarde: a ação anterior ainda está em curso.'
                    : 'Precisa de um contato cadastrado'
                }
              >
                Criar tarefa
              </Botao>
              <Botao
                onClick={() => cliente && aoAbrirCliente(cliente.id)}
                desabilitado={!cliente}
                motivo="Precisa de um contato cadastrado"
              >
                Abrir Cliente 360
              </Botao>
            </div>
            {!cliente && (
              // Botão cinza sem motivo gera chamado no suporte. A regra do
              // sistema visual: desabilitado sempre acompanhado do porquê.
              <p className="contexto__nota">
                As ações precisam de um contato cadastrado — hoje este ainda não tem.
              </p>
            )}
          </Bloco>
        </PainelDaAba>

        {comAgendamento && (
          <PainelDaAba valor="agendamento" ativa={abaAberta}>
            {comConsulta && (
              <Bloco titulo="Consulta">
                <AgendarConsulta
                  oportunidades={paraAgendar}
                  aviso={avisoDaConsulta}
                  aoAbrir={() => {
                    definirAvisoDaConsulta(null);
                    aoAgendando?.(true);
                  }}
                />
              </Bloco>
            )}
            {comPrecos && aoCopiarParaMensagem && (
              <Bloco titulo="Preços">
                <PrecosDaClinica
                  podeAtualizar={podeAtualizarPrecos}
                  aoCopiar={aoCopiarParaMensagem}
                />
              </Bloco>
            )}
          </PainelDaAba>
        )}

        <PainelDaAba valor="atividades" ativa={abaAberta}>
          <Bloco titulo="Oportunidades">
            {oportunidades.length === 0 ? (
              <p className="contexto__nota">Nenhuma oportunidade associada a este cliente.</p>
            ) : (
              <ul className="oportunidades">
                {oportunidades.map((oportunidade) => (
                  <Cartao como="li" variante="interno" key={oportunidade.id}>
                    <div className="cartao__titulo">{oportunidade.titulo ?? 'Sem título'}</div>
                    <div className="cartao__linha">
                      {oportunidade.etapa && (
                        <Chip tom="etapa">
                          {nomeDaEtapa(oportunidade.etapa_nome ?? oportunidade.etapa)}
                        </Chip>
                      )}
                      {dinheiro(oportunidade.valor) && (
                        <span className="cartao__valor">{dinheiro(oportunidade.valor)}</span>
                      )}
                    </div>
                    {oportunidade.stale && (
                      <div className="oportunidade__defasada">
                        Pode estar desatualizada
                        {oportunidade.atualizada_em
                          ? `, última mudança ${idadeEmPalavras(oportunidade.atualizada_em)}`
                          : ''}
                        .
                      </div>
                    )}
                  </Cartao>
                ))}
              </ul>
            )}
          </Bloco>

          {cliente && (
            <Bloco titulo="Tarefas futuras">
              <TarefasFuturas tarefas={tarefasFuturas} />
            </Bloco>
          )}

          {/*
            Só com duas pessoas ou mais (#641): com uma, ela é o responsável que
            já aparece em Detalhes do atendimento, e o bloco só repetiria o nome.
          */}
          {participantes.length > 1 && (
            <Bloco titulo="Quem falou com o cliente">
              <ul className="participantes" aria-label="Pessoas da equipe nesta conversa">
                {participantes.map((pessoa) => (
                  <li key={pessoa.usuario_id} className="participantes__item">
                    <Avatar nome={pessoa.nome ?? 'Sem nome'} tamanho={28} foto={pessoa.foto_url} />
                    <span className="participantes__nome">{pessoa.nome ?? 'Sem nome'}</span>
                  </li>
                ))}
              </ul>
            </Bloco>
          )}
        </PainelDaAba>

        <PainelDaAba valor="notas" ativa={abaAberta}>
          {/* Sem cadastro não há a quem anotar — a nota é do CLIENTE. */}
          {cliente ? (
            /* `key`: trocar de cliente recomeça o campo, e a nota começada para
               um não vai para o outro. */
            <NotasDoCliente key={cliente.id} clienteId={cliente.id} />
          ) : (
            <p className="contexto__nota">
              As notas ficam no cadastro do cliente — e este contato ainda não tem um.
            </p>
          )}
        </PainelDaAba>
      </div>
    </aside>
  );
}

/** O `tabpanel` de uma aba: no DOM sempre, `hidden` quando não é a vez dele. */
function PainelDaAba({
  valor,
  ativa,
  children,
}: {
  valor: AbaDoPainel;
  ativa: AbaDoPainel;
  children: ReactNode;
}): ReactNode {
  return (
    <div
      role="tabpanel"
      id={idDoPainel(ABAS_DO_PAINEL, valor)}
      aria-labelledby={`${ABAS_DO_PAINEL}-${valor}`}
      className="painel-cliente__aba"
      hidden={valor !== ativa}
    >
      {children}
    </div>
  );
}

/**
 * Uma linha da ficha: ícone, rótulo e valor — o desenho do painel.
 *
 * `dl`/`dt`/`dd` para o leitor de tela ler "Telefone: +55…" como um par, e
 * não como três textos soltos. O ícone é `aria-hidden`: o rótulo já diz.
 */
function Linha({
  icone,
  rotulo,
  canal,
  children,
}: {
  icone: NomeDoIcone;
  rotulo: string;
  /** O canal, para o glifo sair na cor da marca (WhatsApp verde). */
  canal?: string | null;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="painel-cliente__linha">
      <span
        className="painel-cliente__linha-icone"
        data-canal={canal ?? undefined}
        aria-hidden="true"
      >
        <Icone nome={icone} />
      </span>
      <dt className="painel-cliente__linha-rotulo">{rotulo}</dt>
      <dd className="painel-cliente__linha-valor">{children}</dd>
    </div>
  );
}

/**
 * Informações: nome, telefone, e-mail, origem, última interação e tags.
 *
 * ── Copiar, porque o destino do telefone é outro aplicativo ──────────────
 *
 * Telefone e e-mail quase nunca são LIDOS aqui: são levados para outro lugar —
 * a agenda, o disparador, um e-mail. Selecionar treze dígitos com o mouse numa
 * coluna estreita erra, e o erro só aparece do outro lado.
 *
 * As tags são as etiquetas DA CONVERSA, com o "+" que as edita — ele saiu do
 * cabeçalho da conversa e veio para cá (v3), que é onde o desenho o põe.
 */
function Informacoes({ conversa }: { conversa: ConversaComposta }): ReactNode {
  const { cliente } = conversa;
  const papel = usePapel();
  const podeEditar = papel !== null && EDITA_CLIENTE.has(papel);
  // O documento só se edita quando a organização o usa na identidade (H18):
  // fora disso a API recusa, e o lápis ofereceria um caminho que termina em erro.
  const { usar_documento_na_identidade: usaDocumento } = usePreferencias();
  const { id, canal, etiquetas, ultima_mensagem_em: ultimaMensagemEm } = conversa.conversa;
  const glifo = iconeDoCanal(canal);
  const nomeDoCanalAtual = nomeDoCanal(canal);
  const ultima = idadeEmPalavras(ultimaMensagemEm);

  return (
    <Bloco titulo="Informações">
      {cliente ? (
        <dl className="painel-cliente__ficha">
          <Linha icone="pessoa" rotulo="Nome">
            {cliente.nome_exibicao ?? <span className="painel-cliente__vazio">sem nome</span>}
          </Linha>
          <Linha icone="telefone" rotulo="Telefone">
            <ContatoEditavel
              campo="telefone"
              clienteId={cliente.id}
              conversaId={id}
              valorAtual={cliente.telefone_exibicao}
              podeEditar={podeEditar}
            >
              {cliente.telefone_exibicao ?? (
                <span className="painel-cliente__vazio">não informado</span>
              )}
            </ContatoEditavel>
          </Linha>
          <Linha icone="email" rotulo="E-mail">
            <ContatoEditavel
              campo="email"
              clienteId={cliente.id}
              conversaId={id}
              valorAtual={cliente.email}
              podeEditar={podeEditar}
            >
              {cliente.email ? (
                <span className="painel-cliente__copiavel">
                  <span className="painel-cliente__valor-texto" title={cliente.email}>
                    {cliente.email}
                  </span>
                  <BotaoDeCopiar rotulo="e-mail" valor={cliente.email} />
                </span>
              ) : (
                <span className="painel-cliente__vazio">não informado</span>
              )}
            </ContatoEditavel>
          </Linha>
          <Linha icone={glifo ?? 'atendimento'} rotulo="Origem" canal={glifo}>
            {nomeDoCanalAtual ?? <span className="painel-cliente__vazio">desconhecida</span>}
          </Linha>
          <Linha icone="relogio" rotulo="Última interação">
            {ultima ?? <span className="painel-cliente__vazio">sem mensagem</span>}
          </Linha>
          {/*
            Só a EXISTÊNCIA do documento. Ver o número, mesmo mascarado, exige
            o step-up da B17 — e esta tela fica aberta o dia inteiro, muitas
            vezes com gente passando atrás.
          */}
          <Linha icone="identidade" rotulo="Documento">
            <ContatoEditavel
              campo="documento"
              clienteId={cliente.id}
              conversaId={id}
              valorAtual={null}
              podeEditar={podeEditar && usaDocumento}
            >
              <span className="painel-cliente__vazio">
                {cliente.documento.presente
                  ? 'Documento cadastrado · exige confirmação para ver'
                  : 'Documento não informado'}
              </span>
            </ContatoEditavel>
          </Linha>
          <Linha icone="etiqueta" rotulo="Tags">
            <span className="painel-cliente__tags">
              {etiquetas && etiquetas.length > 0 ? (
                <Etiquetas titulos={etiquetas} />
              ) : (
                <span className="painel-cliente__vazio">nenhuma</span>
              )}
              <EditorDeEtiquetasDaConversa conversaId={id} aplicadas={etiquetas ?? []} />
            </span>
          </Linha>
        </dl>
      ) : (
        <p className="contexto__nota">
          Este contato ainda não tem cadastro. Ele é criado quando a identidade for confirmada.
        </p>
      )}
    </Bloco>
  );
}

/**
 * Responsável, prioridade, fila e canal.
 *
 * O seletor de responsável é o mesmo de antes — só mudou de bloco. Era um
 * nome e um botão "Trocar", com a lista de gente escondida atrás de um
 * diálogo; ver quem existe é a metade mais usada da pergunta — "para quem eu
 * passo isto?". Fila e canal são leitura: a fila pelo nome que a projeção
 * guarda (#463), e "—" quando ela não veio.
 */
function DetalhesDoAtendimento({ conversa }: { conversa: ConversaComposta }): ReactNode {
  const {
    id,
    responsavel,
    prioridade,
    caixa_nome: caixaNome,
    canal,
    aberta_em: abertaEm,
  } = conversa.conversa;
  const caixa = caixaNome?.trim();
  const glifo = iconeDoCanal(canal);
  const nomeDoCanalAtual = nomeDoCanal(canal);
  return (
    <dl className="painel-cliente__ficha">
      <Linha icone="pessoa" rotulo="Responsável">
        {/*
          A foto de quem atende (061) ao lado do seletor, para quem atende ser
          reconhecido de relance, sem ler o nome.
        */}
        <span className="acoes-da-conversa__campo acoes-da-conversa__campo--com-foto">
          {responsavel && (
            <Avatar
              nome={responsavel.nome ?? responsavel.referencia_externa}
              tamanho={28}
              foto={responsavel.foto_url}
            />
          )}
          <SeletorDeResponsavel
            conversaId={id}
            rotuloOculto
            nomeAtual={
              responsavel
                ? (responsavel.nome ?? `Atendente ${responsavel.referencia_externa}`)
                : null
            }
          />
        </span>
      </Linha>
      {/*
        Seletor, e não rótulo. Ele era leitura porque não havia rota — e a nota
        dizia para mudar "no atendimento", que queria dizer no motor de
        conversa, por fora. Veio da aba "Mais" para cá.
      */}
      <Linha icone="prioridade" rotulo="Prioridade">
        <span className="acoes-da-conversa__campo">
          <SeletorDePrioridade conversaId={id} prioridade={prioridade} rotuloOculto />
        </span>
      </Linha>
      <Linha icone="caixa" rotulo="Fila">
        {caixa || <span className="painel-cliente__vazio">—</span>}
      </Linha>
      <Linha icone={glifo ?? 'atendimento'} rotulo="Canal" canal={glifo}>
        {nomeDoCanalAtual ?? <span className="painel-cliente__vazio">—</span>}
      </Linha>
      {/* Veio do cabeçalho da conversa (v3): "aberta em 26/09 às 08:51". */}
      <Linha icone="relogio" rotulo="Aberta">
        {abertaPorExtenso(abertaEm)}
      </Linha>
    </dl>
  );
}

/**
 * O botão de copiar: só o ícone, e o rótulo no nome acessível porque há mais
 * de um — "Copiar telefone", "Copiar e-mail". Diz "copiado" depois de copiar e
 * não volta atrás: é a única confirmação que existe, não há som nem toast.
 */
function BotaoDeCopiar({ rotulo, valor }: { rotulo: string; valor: string }): ReactNode {
  const [copiado, definirCopiado] = useState(false);

  return (
    <button
      type="button"
      className="painel-cliente__copiar"
      aria-label={`Copiar ${rotulo}`}
      title={copiado ? 'Copiado' : `Copiar ${rotulo}`}
      data-copiado={copiado ? 'sim' : undefined}
      onClick={() => {
        void navigator.clipboard?.writeText(valor).then(() => definirCopiado(true));
      }}
    >
      <Icone nome="copiar" />
      <span className="visualmente-oculto">{copiado ? 'copiado' : 'copiar'}</span>
    </button>
  );
}

function TarefasFuturas({ tarefas }: { tarefas: ConversaComposta['tarefas_futuras'] }): ReactNode {
  if (tarefas.itens.length === 0) {
    return (
      <p className="contexto__nota">
        {tarefas.stale
          ? 'Não foi possível ver as tarefas agora. Tente reabrir a conversa.'
          : 'Nenhuma tarefa pendente com este cliente.'}
      </p>
    );
  }

  return (
    <ul className="tarefas-futuras">
      {tarefas.itens.map((tarefa) => {
        const prazo = prazoEmPalavras(tarefa.prazo_em);
        return (
          <li key={tarefa.referencia_externa} className="tarefas-futuras__item">
            <span className="tarefas-futuras__titulo">{tarefa.titulo ?? 'Sem título'}</span>
            <span
              className={
                prazo?.passou
                  ? 'tarefas-futuras__prazo tarefas-futuras__prazo--passou'
                  : 'tarefas-futuras__prazo'
              }
            >
              {prazo ? prazo.texto : 'sem prazo'}
              {tarefa.sem_responsavel && ' · sem responsável'}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function BlocoDeVinculo({ vinculo }: { vinculo: ConversaComposta['vinculo'] }): ReactNode {
  /* Identificado é o caso normal: o painel inteiro já diz isso (v3). */
  if (vinculo.estado === 'vinculado') return null;

  if (vinculo.estado === 'em_revisao') {
    return (
      <div className="vinculo vinculo--revisao">
        <strong>Identidade em revisão</strong>
        <p>
          Encontramos mais de um cadastro que pode ser esta pessoa, e preferimos não escolher
          sozinhos — juntar os errados é difícil de desfazer. Alguém da equipe resolve na fila de
          revisão, e o painel se completa aqui.
        </p>
      </div>
    );
  }

  return (
    <div className="vinculo vinculo--sem">
      <strong>Ainda não sabemos quem é</strong>
      <p>
        Este contato não bateu com nenhum cadastro. O histórico e as oportunidades aparecem aqui
        assim que ele for identificado.
      </p>
      {/*
        Sem a ação, NÃO há frase no lugar dela.
        A ausência do botão já é a resposta. O texto que estava aqui dizia
        quando o vínculo manual chegaria — cronograma nosso numa tela de quem
        atende, e o tipo de coisa que faz o produto parecer inacabado.
      */}
      {vinculo.pode_vincular_manualmente && (
        <button className="vinculo__acao" type="button">
          Vincular a um cliente
        </button>
      )}
    </div>
  );
}

/**
 * Um bloco do painel, que recolhe.
 *
 * ── Por que recolher, e por que nasce ABERTO ─────────────────────────────
 *
 * O painel tem blocos numa coluna estreita, e quem atende usa dois ou três
 * por conversa. Recolher devolve altura para o que importa naquele
 * atendimento.
 *
 * Nasce aberto porque bloco fechado é bloco que não existe: quem senta pela
 * primeira vez precisa ver o que há antes de decidir o que esconder. É a
 * escolha oposta à dos filtros da lista (#443), e pelo motivo oposto — lá o
 * risco era filtro ligado e escondido mentir sobre a lista; aqui esconder não
 * altera nada, só economiza rolagem.
 *
 * O conteúdo some com `hidden`, e não desmontando. Desmontado, o foco do
 * teclado cairia no nada, e o estado de quem estava no meio de um editor de
 * etiquetas se perderia junto.
 */
/** O tempo do recolher, o mesmo de `--transicao-entrada`: depois dele o corpo sai do DOM de leitura. */
const RECOLHER_MS = 260;

/*
 * O bloco recolhível, com a altura animada (27/10): abrir e fechar deslizam,
 * em vez de o conteúdo sumir de uma vez e o resto do painel pular.
 *
 * A altura anima pelo `grid-template-rows` (de `1fr` a `0fr`), que vai até a
 * altura do conteúdo sem medi-la. Fechado de vez, o corpo ganha `hidden` e sai
 * da leitura de tela e do Tab, como antes; o `hidden` só chega depois da
 * animação, e sai antes dela ao abrir.
 */
function Bloco({ titulo, children }: { titulo: string; children: ReactNode }): ReactNode {
  const [aberto, definirAberto] = useState(true);
  const [recolhido, definirRecolhido] = useState(false);

  useEffect(() => {
    if (aberto) return;
    const espera = setTimeout(() => definirRecolhido(true), RECOLHER_MS);
    return () => clearTimeout(espera);
  }, [aberto]);

  const alternar = () => {
    if (aberto) {
      definirAberto(false);
      return;
    }
    // Primeiro volta ao DOM fechado (0fr); no quadro seguinte abre, e a altura anima.
    definirRecolhido(false);
    requestAnimationFrame(() => definirAberto(true));
  };

  return (
    <section className="contexto__bloco">
      <h3 className="contexto__titulo">
        <button
          type="button"
          className="contexto__recolher"
          aria-expanded={aberto}
          onClick={alternar}
        >
          <span>{titulo}</span>
          <span className="contexto__sinal" aria-hidden="true">
            <Icone nome="abaixo" />
          </span>
        </button>
      </h3>
      <div className="contexto__corpo" data-aberto={aberto ? 'sim' : 'nao'} hidden={recolhido}>
        <div className="contexto__miolo">{children}</div>
      </div>
    </section>
  );
}
